/**
 * The model of a line while it is edited, and how it goes to and from the API.
 *
 * Out of `LineEditor.tsx` since contract v10.24 because the page needs
 * `payloadWithMargins` too (undoing «Aplicar a todas las líneas»), and a component file
 * that exports functions breaks fast refresh.
 */
import { decimalFor, editableNumber } from './format';
import type { Quote, QuoteLine, QuoteLineWrite } from './types';

/**
 * A line of the editor while it is being edited.
 *
 * Everything is a string, including the numbers, and that is deliberate: an `<input>` holds
 * text, and turning "12," into a number on every keystroke makes the cursor jump and the
 * comma disappear as the user types it. The strings go to the API and the API parses them
 * with `Decimal`.
 *
 * `keep_line_id` is what tells the API "this replaces that line", which is how the cost the
 * supplier gave survives an edit.
 */
export interface DraftLine {
  key: string;
  keep_line_id: string | null;
  description: string;
  supplier_reference: string;
  unit: string;
  quantity: string;
  margin_pct: string;
  discount_pct: string;
  /** Carried, not edited. The API takes it literally from the payload and the totals are
   * computed per rate, so a line dropped to 21 % here would change the customer's total. */
  tax_rate: string;
  /** Carried for the same reason: the API writes what the payload says, and `_changed()`
   * compares it, so losing it would also mark the line as edited by a human. */
  article_code: string | null;
  /** Read only. Shown so somebody can see where the price comes from. */
  net_cost: string | null;
  supplier_unit_price: string | null;
  supplier_discounts: string[] | null;
  price_unit_basis: number;
  /** Computed by the API from the cost and the margin, except on a line with no cost,
   * where it is typed here and sent as `unit_price` (contract v7). */
  unit_price: string;
  line_total: string;
  availability: string | null;
  edited_by_human: boolean;
}

export function fromQuote(line: QuoteLine): DraftLine {
  return {
    key: line.id,
    keep_line_id: line.id,
    description: line.description,
    supplier_reference: line.supplier_reference ?? '',
    unit: line.unit,
    // Formatted for a Spanish reader: `5.000` from the API is five, not five thousand.
    quantity: editableNumber(line.quantity),
    margin_pct: editableNumber(line.margin_pct),
    discount_pct: editableNumber(line.discount_pct),
    tax_rate: line.tax_rate,
    article_code: line.article_code ?? null,
    net_cost: line.net_cost ?? null,
    supplier_unit_price: line.supplier_unit_price ?? null,
    supplier_discounts: line.supplier_discounts ?? null,
    price_unit_basis: line.price_unit_basis ?? 1,
    // Formatted too when it is the typed price of a hand-added line; on a line with a
    // cost the API ignores it and the screen prints it through `unitPrice`.
    unit_price:
      line.net_cost === null || line.net_cost === undefined
        ? editableNumber(line.unit_price)
        : line.unit_price,
    line_total: line.line_total,
    availability: line.availability ?? null,
    edited_by_human: line.edited_by_human ?? false,
  };
}

/**
 * A line with no supplier behind it: added by hand, or one the extractor could not price.
 *
 * It is the one place the price is typed rather than derived (contract v7). Everywhere
 * else the price is cost plus margin, computed by the API, and the payload carries no
 * `unit_price` at all: sending the computed figure back would be a number on the document
 * whose only provenance is "the browser said so".
 */
export function isHandPriced(line: DraftLine): boolean {
  return line.net_cost === null;
}

export function toPayload(lines: DraftLine[]): QuoteLineWrite[] {
  return lines.map((line) => ({
    description: line.description.trim(),
    supplier_reference: line.supplier_reference.trim() || null,
    unit: line.unit.trim() || 'ud',
    quantity: decimalFor(line.quantity),
    margin_pct: line.margin_pct.trim() === '' ? null : decimalFor(line.margin_pct),
    discount_pct: decimalFor(line.discount_pct) || '0',
    tax_rate: decimalFor(line.tax_rate) || '21',
    article_code: line.article_code,
    keep_line_id: line.keep_line_id,
    // Only on a hand-priced line, and `null` when the field was left empty: the API keeps
    // the price the line already had, which is what an edit to the quantity of "Portes"
    // expects. `''` would be a 422.
    ...(isHandPriced(line)
      ? { unit_price: line.unit_price.trim() === '' ? null : decimalFor(line.unit_price) }
      : {}),
  }));
}

/**
 * The lines of `quote` as a save payload, with the margins of `margins` put back.
 *
 * What undoing «Aplicar a todas las líneas» sends (contract v10.24): the global margin
 * clears every per line margin on the server, and the undo is the old default margin
 * (`POST /recalculate`) followed by this, the old per line margins on the same lines.
 * A line the map does not know keeps what the quote says now.
 */
export function payloadWithMargins(
  quote: Quote,
  margins: Record<string, string | null | undefined>,
): QuoteLineWrite[] {
  return toPayload(
    (quote.lines ?? []).map((line) => {
      const draft = fromQuote(line);
      if (!(line.id in margins)) return draft;
      return { ...draft, margin_pct: editableNumber(margins[line.id] ?? null) };
    }),
  );
}
